// Shared box-adding logic for the components panels.

import IdealStudio from "../../../../core/editor/main";
import { isSlide, isBox, isContainedView, isSortableContainer } from "../../../../common/utils";
import {
  ID_PREFIX_BOX,
  ID_PREFIX_SORTABLE_CONTAINER,
  TEMPLATE_SCREEN_TRAIN,
} from "../../../../common/constants";
import { randomPositionGenerator } from "../../clipboard/clipboard.utils";
import { createBox, instanceExists } from "../../../../common/common_tools";
import { getAtomicSampleData } from "../../../../plugins/AtomicBoxPlugin/atomic_box_plugin_config";

const resolvePage = (ctx) => {
  const { containedViews, containedViewSelected, navItems, navItemSelected } = ctx;
  return containedViewSelected && containedViews[containedViewSelected]
    ? containedViews[containedViewSelected]
    : navItems[navItemSelected];
};

// `extra` seeds the new box: { initialState, width, height } (e.g. a Şekiller
// shape carries its shape id and its own default size).
export const addPluginBox = (name, event, ctx, extra = {}) => {
  const { boxes, boxSelected, containedViews, containedViewSelected, navItemSelected, onBoxAdded } =
    ctx;

  const config = IdealStudio.Plugins.get(name)?.getConfig();
  if (!config) return;

  const page = resolvePage(ctx);
  if (!page) return;

  const cv =
    containedViewSelected &&
    containedViews[containedViewSelected] &&
    isContainedView(containedViewSelected);
  const cvslide = cv && isSlide(page.type);
  const cvdoc = cv && !isSlide(page.type);

  const isBoxSelected = boxSelected && boxes[boxSelected] && isBox(boxes[boxSelected]);

  if (isBoxSelected && isBox(boxes[boxSelected].parent) && config.isComplex) {
    event && event.stopPropagation && event.stopPropagation();
    return;
  }

  if (config.limitToOneInstance && instanceExists(name)) {
    event && event.stopPropagation && event.stopPropagation();
    return;
  }

  const inASlide = (!cv && isSlide(page.type)) || cvslide;
  const SelectedNav = inASlide ? page.id : 0;
  const parentBox = inASlide ? 0 : cvdoc ? page.boxes[0] : page.boxes[0];

  const parent = isBoxSelected ? boxes[boxSelected].parent : inASlide ? SelectedNav : parentBox;
  const container = isBoxSelected
    ? boxes[boxSelected].container
    : inASlide
      ? 0
      : ID_PREFIX_SORTABLE_CONTAINER + Date.now();

  const isFullCanvas = config.initialWidth === "100%" && config.initialHeight === "100%";
  const position =
    container === 0
      ? {
          x: isFullCanvas ? 0 : randomPositionGenerator(10, 30),
          y: isFullCanvas ? 0 : randomPositionGenerator(10, 30),
          type: "absolute",
        }
      : undefined;

  const initialParams = {
    parent,
    container,
    name,
    position,
    id: ID_PREFIX_BOX + Date.now(),
    page: cv ? containedViewSelected : page.id || navItemSelected || 0,
  };
  if (extra.initialState) initialParams.initialState = extra.initialState;
  if (extra.width) initialParams.width = extra.width;
  if (extra.height) initialParams.height = extra.height;

  if (!inASlide && isBoxSelected && isSortableContainer(container)) {
    const children = boxes[parent].sortableContainers[container].children;
    let newInd = children.indexOf(boxSelected) + 1;
    newInd = newInd === 0 ? 1 : newInd >= children.length ? children.length : newInd;
    initialParams.index = newInd;
    initialParams.col = 0;
    initialParams.row = 0;
  }

  createBox(initialParams, name, inASlide, onBoxAdded, boxes);

  if (event) {
    event.stopPropagation();
    event.preventDefault();
  }
};

export const addAtomicBox = (elementType, ctx) => {
  const { boxes, containedViews, containedViewSelected, navItemSelected, onBoxAdded } = ctx;

  const pluginName = "AtomicBoxPlugin";
  const config = IdealStudio.Plugins.get(pluginName)?.getConfig();
  if (!config) return;

  const page = resolvePage(ctx);
  if (!page) return;

  const cv =
    containedViewSelected &&
    containedViews[containedViewSelected] &&
    isContainedView(containedViewSelected);
  const cvslide = cv && isSlide(page.type);
  const inASlide = (!cv && isSlide(page.type)) || cvslide;

  const initialParams = {
    parent: inASlide ? page.id : page.boxes?.[0] || 0,
    container: 0,
    name: pluginName,
    position: {
      x: randomPositionGenerator(10, 30),
      y: randomPositionGenerator(10, 30),
      type: "absolute",
    },
    id: ID_PREFIX_BOX + Date.now(),
    page: cv ? containedViewSelected : page.id || navItemSelected || 0,
    initialState: {
      __text: encodeURIComponent(JSON.stringify({ elementData: getAtomicSampleData(elementType) })),
    },
    width: config.initialWidth || "40%",
    height: config.initialHeight || "auto",
  };

  createBox(initialParams, pluginName, inASlide, onBoxAdded, boxes);
};

export const addHtmlBox = (htmlContent, templateId, ctx) => {
  const { boxes, containedViews, containedViewSelected, navItems, navItemSelected, onBoxAdded } =
    ctx;

  const name = "HtmlContent";
  const config = IdealStudio.Plugins.get(name)?.getConfig();
  if (!config) return;

  const page = resolvePage(ctx);
  if (!page) return;

  const cv =
    containedViewSelected &&
    containedViews[containedViewSelected] &&
    isContainedView(containedViewSelected);
  const cvslide = cv && isSlide(page.type);
  const inASlide = (!cv && isSlide(page.type)) || cvslide;

  const isScreenTraining = templateId === TEMPLATE_SCREEN_TRAIN;
  const initialParams = {
    parent: inASlide ? page.id : page.boxes?.[0] || 0,
    container: 0,
    name,
    position: { x: "0%", y: "0%", type: "absolute" },
    id: ID_PREFIX_BOX + Date.now(),
    page: cv ? containedViewSelected : page.id || navItemSelected || 0,
    initialState: { _html: htmlContent, _templateId: templateId },
    width: isScreenTraining ? "100%" : "50%",
    height: isScreenTraining ? "100%" : "30%",
  };

  createBox(initialParams, name, inASlide, onBoxAdded, boxes);
};
